import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Paper, Typography, Box, Card, CardContent, Avatar, List, ListItem, ListItemIcon, ListItemText, Alert, Chip, Divider, IconButton } from '@mui/material';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function SupportDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const firstName = (localStorage.getItem('full_name') || 'User').split(' ')[0];
  
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(`${API_URL}/it-projects/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return <Loading message="Loading IT Dashboard..." />;

  // Doughnut Data (Vibrant Colors)
  const doughnutData = {
    labels: Object.keys(stats.status_distribution),
    datasets: [
      {
        data: Object.values(stats.status_distribution),
        backgroundColor: ['#F59E0B', '#94A3B8', '#EF4444', '#10B981', '#3B82F6'],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverOffset: 4
      },
    ],
  };

  // Bar Data (Clean Blue)
  // Using avg_progress or other metrics if available. For now just avg_progress
  const barData = {
    labels: ['Average Progress'],
    datasets: [
      {
        label: 'Percentage',
        data: [stats.avg_progress],
        backgroundColor: '#6366F1',
        borderRadius: 6,
        barThickness: 40,
      },
    ],
  };


  // Clean Stat Card Component
  const StatCard = ({ title, value, subtitle, icon, color }) => (
    <Paper elevation={0} sx={{ height: '100%', p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="caption" fontWeight="bold" color="textSecondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
            <Box sx={{ color: color, p: 0.5, borderRadius: 2, bgcolor: `${color}15` }}>
                {icon}
            </Box>
        </Box>
        <Typography variant="h5" fontWeight="800" sx={{ color: '#1e293b', mb: 0.5 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: subtitle?.includes('Risk') ? '#F59E0B' : (subtitle?.includes('Active') ? '#10B981' : 'textSecondary'), fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {subtitle}
        </Typography>
    </Paper>
  );

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', p: 1 }}>
      
      {/* Welcome Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', mb: 1 }}>
            IT Projects Dashboard 🖥️
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
            Overview of IT projects and status.
        </Typography>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 7 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Projects" 
            value={stats.total_projects} 
            subtitle="IT Projects" 
            icon={<FolderIcon fontSize="medium" />} 
            color="#3B82F6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Active" 
            value={stats.active_projects} 
            subtitle="Ongoing" 
            icon={<CheckCircleIcon fontSize="medium" />} 
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="At Risk" 
            value={stats.at_risk} 
            subtitle="Overdue" 
            icon={<WarningIcon fontSize="medium" />} 
            color="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Avg Progress" 
            value={`${stats.avg_progress}%`} 
            subtitle="Overall Completion" 
            icon={<CheckIcon fontSize="medium" />} 
            color="#6366F1"
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        
        {/* Left Column: Project Performance (2/3 width) */}
        <Grid item xs={12} lg={8}>
            
            <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                
                {/* Header */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Status Distribution</Typography>
                    <Typography variant="caption" color="textSecondary">Current condition status across all IT projects</Typography>
                </Box>

                <Grid container spacing={4}>
                    
                    {/* Doughnut + Summary */}
                    <Grid item xs={12} md={6} sx={{ mb: 4 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12}>
                                <Box sx={{ height: 220, display: 'flex', justifyContent: 'center' }}>
                                    <Doughnut 
                                        data={doughnutData} 
                                        options={{ 
                                            cutout: '75%', 
                                            plugins: { legend: { display: false } } 
                                        }} 
                                    />
                                </Box>
                            </Grid>
                        
                        </Grid>
                    </Grid>

                    {/* Bar Chart Section */}
                    <Grid item xs={12} md={6}>
                         <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Average Progress</Typography>
                         <Box sx={{ height: 250 }}>
                            <Bar 
                                data={barData} 
                                options={{ 
                                    responsive: true, 
                                    maintainAspectRatio: false,
                                    scales: { 
                                        y: { display: true, beginAtZero: true, max: 100, grid: { borderDash: [4, 4] } }, 
                                        x: { grid: { display: false } } 
                                    },
                                    plugins: { legend: { display: false } }
                                }} 
                            />
                         </Box>
                    </Grid>

                </Grid>
            </Paper>

        </Grid>

        {/* Right Column: Upcoming Milestones (1/3 width) */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>                   
            {/* Upcoming Milestones */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Upcoming Milestones</Typography>
                        <Typography variant="caption" color="textSecondary">Milestones due in next 7 days</Typography>
                    </Box>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 && (
                        <Chip label={stats.weekly_schedule.length} size="small" color="error" sx={{ fontWeight: 'bold' }} />
                    )}
                </Box>
                
                <Paper elevation={0} sx={{ p: 0, bgcolor: 'transparent' }}>
                    {stats.weekly_schedule && stats.weekly_schedule.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {stats.weekly_schedule.map((entry) => (
                                <Paper 
                                    key={entry.title + entry.id} 
                                    elevation={0}
                                    sx={{ 
                                        p: 2, 
                                        borderRadius: 3, 
                                        bgcolor: '#FFF7ED', 
                                        border: '1px solid #FED7AA',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': { transform: 'translateY(-2px)' }
                                    }}
                                    onClick={() => navigate(`/it-projects/${entry.id}`)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Chip 
                                            label={entry.status} 
                                            size="small" 
                                            sx={{ 
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: 'white',
                                                color: '#EA580C',
                                                border: '1px solid #FFEDD5'
                                            }} 
                                        />
                                    </Box>
                                    
                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#9A3412', mb: 0.5 }}>
                                        {entry.title}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ color: '#C2410C', mb: 2, lineHeight: 1.4 }}>
                                        Project: {entry.project_name}
                                    </Typography>

                                    <Grid container justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="caption" display="block" sx={{ color: '#9A3412', opacity: 0.7 }}>Due Date</Typography>
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#9A3412' }}>
                                                {new Date(entry.start_time).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'white', borderRadius: 4, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="body2" color="textSecondary">No upcoming milestones.</Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
          </Paper>
        </Grid>

      </Grid>

      {/* Risk Projects Grid */}
      <Grid container spacing={4} sx={{ mt: 7 }}>
        
            {/* Risk Projects (Alerts) */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Risk Projects</Typography>
                    <Typography variant="caption" color="textSecondary">IT Projects requiring immediate attention</Typography>
                </Box>
                {stats.alerts && stats.alerts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.alerts.map((alert, index) => (
                            <Paper 
                                key={index} 
                                elevation={0} 
                                sx={{ 
                                    p: 2, 
                                    borderRadius: 3, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'white',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }
                                }}
                                onClick={() => navigate(`/it-projects/${alert.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Project Alert</Typography>
                                    <Chip 
                                        label="Overdue" 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: '#DC2626', 
                                            color: 'white', 
                                            fontWeight: 'bold', 
                                            borderRadius: 1.5,
                                            height: 24
                                        }} 
                                    />
                                </Box>
                                
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    {alert.message}
                                </Typography>

                                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary">Due Date:</Typography>
                                    <Typography variant="caption" color="textSecondary">{alert.date}</Typography>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                         <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                         <Typography variant="body2" color="textSecondary">No IT projects currently at risk.</Typography>
                    </Paper>
                )}
              </Paper>
            </Grid>

            {/* Active Tickets */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Active Tickets</Typography>
                    <Typography variant="caption" color="textSecondary">Your assigned ticket queue</Typography>
                </Box>
                 {stats.my_tickets && stats.my_tickets.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.my_tickets.map((ticket) => (
                            <Paper 
                                key={ticket.id} 
                                elevation={0} 
                                sx={{ 
                                    p: 2, 
                                    borderRadius: 3, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderColor: '#3B82F6' }
                                }}
                                onClick={() => navigate(`/tickets/${ticket.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">{ticket.title}</Typography>
                                    <Chip 
                                        label={ticket.status} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: ticket.status === 'In Progress' ? '#2563EB' : 
                                                     ticket.status === 'Done' ? '#10B981' :
                                                     ticket.status === 'Reopen' ? '#DC2626' : 
                                                     '#D97706', 
                                            color: 'white', 
                                            fontWeight: 'bold', 
                                            borderRadius: 1.5,
                                            height: 24
                                        }} 
                                    />
                                </Box>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Chip 
                                        label={ticket.type} 
                                        size="small" 
                                        variant="outlined" 
                                        sx={{ 
                                            color: '#EA580C', 
                                            borderColor: '#EA580C', 
                                            fontWeight: 'bold', 
                                            borderRadius: 1.5,
                                            height: 22,
                                            fontSize: '0.7rem'
                                        }} 
                                    />
                                </Box>

                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
                                     ID: #{String(ticket.id).padStart(5, '0')} • Assigned to you
                                </Typography>

                                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary">Action Required</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#2563EB' }}>
                                        <Typography variant="caption" fontWeight="bold">View Ticket</Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 12 }} />
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                         <CheckCircleIcon color="success" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                         <Typography variant="body2" color="textSecondary">No active tickets.</Typography>
                    </Paper>
                )}
              </Paper>
            </Grid>

            {/* Expiring Licenses */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: 'white', border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Expiring Licenses</Typography>
                    <Typography variant="caption" color="textSecondary">Renewals due in 30 days</Typography>
                </Box>
                 {stats.expiring_licenses && stats.expiring_licenses.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {stats.expiring_licenses.map((license) => (
                            <Paper 
                                key={license.id} 
                                elevation={0} 
                                sx={{ 
                                    p: 2, 
                                    borderRadius: 3, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: '#FFF7ED', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderColor: '#F97316' }
                                }}
                                onClick={() => navigate(`/software-licenses/${license.id}`)}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">{license.name}</Typography>
                                    <Chip 
                                        label="Expiring" 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: '#F97316', 
                                            color: 'white', 
                                            fontWeight: 'bold', 
                                            borderRadius: 1.5,
                                            height: 24
                                        }} 
                                    />
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                    <VerifiedUserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="textSecondary">{license.supplier}</Typography>
                                </Box>

                                <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" fontWeight="bold" color="textSecondary">Expires On:</Typography>
                                    <Typography variant="caption" color="#C2410C" fontWeight="bold">{license.expiry_date}</Typography>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                         <VerifiedUserIcon color="success" sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                         <Typography variant="body2" color="textSecondary">No licenses expiring soon.</Typography>
                    </Paper>
                )}
              </Paper>
            </Grid>
      </Grid>

    </Box>
  );
}

export default SupportDashboard;
